import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getUserGroups } from "@/services/ms-graph-api.service";
import { msalApp } from "@/constants/auth.constant";
import {
  CHOP_GENAI_DEVELOPERS_GROUP,
  CHOP_AI_ASSISTANT_USERS_PROD_GROUP,
  CHOP_AI_ASSISTANT_USERS_GROUP,
  POLICY_ASSISTANT_USERS_GROUP,
} from "@/constants/auth.constant";

interface UserGroup {
  id: string;
  name: string;
}

interface UserGroupsContextValue {
  groups: UserGroup[];
  isLoading: boolean;
  isInGroup: (groupId: string) => boolean;
  canAccessCurrentEnv: boolean;
  canAccessChopGpt: boolean;
  canAccessPolicyAssistant: boolean;
}

const UserGroupsContext = createContext<UserGroupsContextValue>({
  groups: [],
  isLoading: true,
  isInGroup: () => false,
  canAccessCurrentEnv: false,
  canAccessChopGpt: false,
  canAccessPolicyAssistant: false,
});

export function UserGroupsProvider({ children }: { children: React.ReactNode }) {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getUserGroups(msalApp)
      .then((fetchedGroups) => {
        setGroups(fetchedGroups);
      })
      .catch((err) => console.error("Failed to fetch user groups:", err))
      .finally(() => setIsLoading(false));
  }, []);

  const isInGroup = useCallback(
    (groupId: string): boolean => groups.some((g) => g.id === groupId),
    [groups]
  );

  const canAccessCurrentEnv = useMemo(() => {
    const env = import.meta.env.VITE_ENV;
    if (env === "dev" || env === "uat") {
      return isInGroup(CHOP_GENAI_DEVELOPERS_GROUP);
    }
    return (
      isInGroup(CHOP_AI_ASSISTANT_USERS_PROD_GROUP) ||
      isInGroup(CHOP_AI_ASSISTANT_USERS_GROUP) ||
      isInGroup(POLICY_ASSISTANT_USERS_GROUP)
    );
  }, [isInGroup]);

  const canAccessChopGpt = useMemo(() => {
    return isInGroup(CHOP_AI_ASSISTANT_USERS_PROD_GROUP) || isInGroup(CHOP_AI_ASSISTANT_USERS_GROUP);
  }, [isInGroup]);

  const canAccessPolicyAssistant = useMemo(() => {
    return isInGroup(POLICY_ASSISTANT_USERS_GROUP);
  }, [isInGroup]);

  return (
    <UserGroupsContext.Provider
      value={{
        groups,
        isLoading,
        isInGroup,
        canAccessCurrentEnv,
        canAccessChopGpt,
        canAccessPolicyAssistant,
      }}
    >
      {children}
    </UserGroupsContext.Provider>
  );
}

export function useUserGroups(): UserGroupsContextValue {
  return useContext(UserGroupsContext);
}
